/*
 * Product description "Read more / Read less".
 *
 * The Liquid snippet renders BOTH panes — a server-truncated copy (visible) and
 * the full rich text (hidden) — so the collapsed state is already correct on
 * first paint. This element only swaps which pane is hidden; it never measures
 * or rewrites content, so there is no flash or layout jump on load.
 *
 * Attributes consumed:
 *   data-collapse-enabled  "true" | "false" — when "false", expanding is a
 *                          one-way action and the toggle removes itself.
 */

if (!customElements.get('product-description-readmore')) {
  customElements.define(
    'product-description-readmore',
    class ProductDescriptionReadmore extends HTMLElement {
      #button = null;
      #truncatedPane = null;
      #fullPane = null;
      #moreLabel = null;
      #lessLabel = null;

      #onToggle = () => this.#setExpanded(this.#button.getAttribute('aria-expanded') !== 'true');

      connectedCallback() {
        this.#button = this.querySelector('[data-toggle]');
        this.#truncatedPane = this.querySelector('[data-pane="truncated"]');
        this.#fullPane = this.querySelector('[data-pane="full"]');
        this.#moreLabel = this.querySelector('[data-label="more"]');
        this.#lessLabel = this.querySelector('[data-label="less"]');

        if (!this.#button || !this.#truncatedPane || !this.#fullPane) return;

        // connectedCallback re-runs when the theme editor re-renders the section
        // or the node is moved, so keep exactly one listener.
        this.#button.removeEventListener('click', this.#onToggle);
        this.#button.addEventListener('click', this.#onToggle);
      }

      disconnectedCallback() {
        if (this.#button) this.#button.removeEventListener('click', this.#onToggle);
      }

      #setExpanded(expanded) {
        this.#truncatedPane.hidden = expanded;
        this.#fullPane.hidden = !expanded;
        this.#button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

        if (this.#moreLabel) this.#moreLabel.hidden = expanded;
        if (this.#lessLabel) this.#lessLabel.hidden = !expanded;

        // Read less disabled: expanding is permanent, so retire the control and
        // hand focus back to the description that just appeared.
        if (expanded && this.dataset.collapseEnabled === 'false') {
          this.#button.hidden = true;
          this.#fullPane.setAttribute('tabindex', '-1');
          this.#fullPane.focus();
        }
      }
    }
  );
}
